import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { t } from '../../../../types/translation/Translator';
import { Task } from '../../../../types/task';
import { StockLocation } from '../../../../types/stock_location';
import { User } from '../../../../types/user';
import { BinStatus } from '../../../../types/bin';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { itemsContainText } from '../../../../util/string.util';
import { FilterProps, FilterStorageKey } from '../common/filter.util';
import { BaseFilter } from '../common/BaseFilter';
import { DateTimeFilter } from '../DateTimeFilter';
import { StockLocationFilter } from '../StockLocationFilter';
import { CountryFilter } from '../CountryFilter';
import { BinFilter } from '../BinFilter/BinFilter';
import { ProductMasterDataFilter } from '../ProductMasterDataFilter';
import { TagFilter } from '../TagFilter/TagFilter';
import { UserFilter } from '../UserFilter/UserFilter';
import { TaskStatusFilter } from './TaskStatusFilter';
import { TaskTypeFilter } from './TaskTypeFilter';
import { CustomField } from '../../../../types/custom_field/customField';
import { EntityType } from '../../../../types/comment';
import { Bin } from '../../../../types/bin';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';

export class TaskFilter extends BaseFilter<Task> {
  stockLocations: Map<string, StockLocation>;
  users: Map<string, User>;
  binStatuses: Map<string, BinStatus>;
  productMasterData: Map<string, ProductMasterData>;
  tagRelations: Map<string, TagRelation>;
  customFields: Map<string, CustomField>;
  bins: Map<string, Bin>;
  company: Company;

  constructor(
    stockLocations: Map<string, StockLocation>,
    users: Map<string, User>,
    binStatuses: Map<string, BinStatus>,
    productMasterData: Map<string, ProductMasterData>,
    tagRelations: Map<string, TagRelation>,
    customFields: Map<string, CustomField>,
    bins: Map<string, Bin>,
    company: Company,
    key?: string,
  ) {
    super(FilterStorageKey.TASK, key);
    this.stockLocations = stockLocations;
    this.users = users;
    this.binStatuses = binStatuses;
    this.productMasterData = productMasterData;
    this.tagRelations = tagRelations;
    this.customFields = customFields;
    this.bins = bins;
    this.company = company;
  }

  filterOptions = () => [
    ...(this.company.partner ? [new CompanyPartnerFilter()] : []),
    new DateTimeFilter(),
    new CountryFilter(),
    new StockLocationFilter(this.tagRelations, this.company),
    new UserFilter(this.users),
    new BinFilter(this.binStatuses, this.tagRelations, this.customFields, this.bins, this.company, this.stockLocations),
    new ProductMasterDataFilter(this.customFields, this.tagRelations, this.company),
    new TaskStatusFilter(this.stockLocations),
    new TaskTypeFilter(this.stockLocations),
    new TagFilter(EntityType.stockLocation),
  ];

  toLabel(): string {
    return t(TranslationKey.task).plural.label;
  }

  toInnerContent(): string | JSX.Element {
    return <></>;
  }

  toIcon(): React.JSX.Element {
    return <></>;
  }

  toDropdownContent(): React.JSX.Element {
    return <></>;
  }

  toCount(): number {
    return 0;
  }

  search(item: Task, filter: string) {
    return itemsContainText(filter, [
      this.stockLocations.get(item.stockLocationId)?.name,
      item.type,
      item.status,
      item.number,
    ]);
  }

  filteredItems(items: Task[], query: string, filterProps: FilterProps): Task[] {
    return items
      .filter(item => {
        return [
          DateTimeFilter.filter(filterProps, new Date(item.createdAt)),
          TagFilter.filterBy(filterProps, EntityType.stockLocation, this.tagRelations, item.stockLocationId),
          StockLocationFilter.filter(filterProps, this.stockLocations.get(item.stockLocationId)),
          TaskFilter.filter(filterProps, item, this.customFields, this.stockLocations),
          this.search(item, query),
        ].every(value => value === null || value);
      })
      .sort((a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf());
  }

  static filter(
    filterProps: FilterProps,
    item?: Task,
    customFields: Map<string, CustomField> = new Map(),
    stockLocations: Map<string, StockLocation> = new Map(),
  ) {
    if (item && filterProps.taskType?.size && !filterProps.taskType.has(item.type)) return false;

    if (
      item &&
      filterProps.product?.size &&
      !item.identifiers
        .filter(i => i.type === EntityType.pmd)
        .flatMap(i => i.ids)
        .filter(i => filterProps.product?.has(i)).length
    ) {
      return false;
    }

    if (
      item &&
      filterProps.bin?.size &&
      !item.identifiers
        .filter(i => i.type === EntityType.bin)
        .flatMap(i => i.ids)
        .filter(i => filterProps.bin?.has(i)).length
    ) {
      return false;
    }

    if (item && filterProps.user?.size && !item.assignedTo.filter(i => filterProps.user?.has(i)).length) {
      return false;
    }

    if (item && filterProps.country?.size) {
      const stockLocation = stockLocations.get(item.stockLocationId);
      if (!filterProps.country.has(stockLocation?.address?.countryCode || '')) return false;
    }

    if (item && filterProps.taskStatus?.size && !filterProps.taskStatus.has(item.status)) {
      return false;
    }

    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) {
      return false;
    }

    return true;
  }
}
